import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';

/**
 * Reusable Delete Confirmation Modal
 * Supports optional PIN verification for sensitive deletions
 * 
 * @param {boolean} open - Whether modal is open
 * @param {Function} onOpenChange - Handler for open state changes
 * @param {string} title - Modal title
 * @param {string} description - Description text
 * @param {Object} item - Item being deleted (for display)
 * @param {Array} displayFields - Fields to show from item [{label, key}]
 * @param {boolean} requirePin - Whether PIN is required
 * @param {string} pin - Current PIN value
 * @param {Function} onPinChange - PIN change handler
 * @param {Function} onConfirm - Confirm delete handler
 * @param {boolean} loading - Loading state
 * @param {string} confirmText - Confirm button text
 */
export default function DeleteConfirmationModal({
  open,
  onOpenChange,
  title = 'Delete Item',
  description = 'This action cannot be undone.',
  item,
  displayFields = [],
  requirePin = false,
  pin = '',
  onPinChange,
  onConfirm,
  loading = false,
  confirmText = 'Delete'
}) {
  const handleClose = () => {
    onOpenChange(false);
  };

  const isDisabled = loading || (requirePin && !pin);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800" data-testid="delete-confirmation-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-500" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {description}
          </DialogDescription>
        </DialogHeader>

        {item && displayFields.length > 0 && (
          <div className="space-y-4">
            <div className="bg-slate-800 rounded-lg p-4">
              {displayFields.map((field, index) => (
                <div key={field.key} className={index > 0 ? 'mt-2' : ''}>
                  <p className="text-slate-400 text-sm">{field.label}:</p>
                  <p className={`text-white ${field.mono ? 'font-mono' : ''}`}>
                    {item[field.key] || '-'}
                  </p>
                </div>
              ))}
            </div>

            {requirePin && (
              <div className="space-y-2">
                <Label className="text-white">Deletion PIN</Label>
                <Input
                  type="password"
                  placeholder="Enter company PIN"
                  value={pin}
                  onChange={(e) => onPinChange(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="delete-pin-input"
                />
                <p className="text-slate-500 text-xs">Contact your admin if you do not know the PIN</p>
              </div>
            )}
          </div>
        )}

        {/* Warning for items without details */}
        {!item && (
          <div className="flex items-center gap-3 p-4 bg-red-900/20 rounded-lg border border-red-500/30">
            <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-red-300 text-sm">
              Are you sure you want to delete this item? This action cannot be undone.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="border-slate-700"
          >
            Cancel
          </Button>
          <Button 
            onClick={onConfirm}
            disabled={isDisabled}
            className="bg-red-600 hover:bg-red-500"
            data-testid="confirm-delete-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
